import "./QuestionForm.scss";

import { useCallback, useRef, useState } from "react";
import { Button, OverlayTrigger, Spinner } from "react-bootstrap";
import { animateScroll as scroll } from "react-scroll";
import type { Descendant, Editor } from "slate";
import { ReactEditor } from "slate-react";

import { RefreshIcon } from "../../../../components/icons";
import { Helpers } from "../../../../components/SlateEditor/helpers";
import { SlateEditor } from "../../../../components/SlateEditor/SlateEditor";
import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";

const isMacOS = navigator.platform.includes("Mac");

const DEFAULT_VALUE = [{ type: "paragraph", children: [{ text: "" }] }];

interface QuestionFormProps {
  isLoading: boolean;
  isEmptyState: boolean;
  hasApiKey: boolean;
  sendQuestion: (question: string | null) => void;
  resetState: () => void;
  addQuestion: (question: string) => void;
  disabledSubmit?: boolean;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  isLoading,
  isEmptyState,
  hasApiKey,
  sendQuestion,
  resetState,
  addQuestion,
  disabledSubmit,
}) => {
  const inputRef = useRef<Editor>(null);

  const [editorValue, setEditorValue] = useState<Descendant[] | null>(null);
  const [defaultValue, setDefaultValue] = useState(DEFAULT_VALUE);

  const runRequest = () => {
    if (isLoading || !hasApiKey || disabledSubmit) {
      return;
    }
    if (!editorValue) {
      sendQuestion(null);
      return;
    }
    sendQuestion(Helpers.slate2markdown(editorValue));

    setDefaultValue([...defaultValue]);

    setEditorValue(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && hasApiKey) {
      runRequest();
      setTimeout(() => {
        if (inputRef.current && !isLoading) {
          ReactEditor.focus(inputRef.current);
          inputRef.current.select({
            anchor: { path: [0, 0], offset: 0 },
            focus: { path: [0, 0], offset: 0 },
          });
        }
      });
    }
  };

  const handleAddMessage = () => {
    if (!editorValue) return;
    addQuestion(Helpers.slate2markdown(editorValue));
    setDefaultValue([...defaultValue]);
    setEditorValue(null);
    setTimeout(() => {
      scroll.scrollToBottom({
        containerId: "chat-container",
        duration: 200,
        smooth: true,
        ignoreCancelEvents: false,
      });
    });
  };

  const handleEditMessage = useCallback((value: Descendant[] | null) => {
    setEditorValue(value);
  }, []);

  return (
    <div className="playground-form-wrapper">
      <div className="playground-form">
        <SlateEditor
          isLoading={false}
          defaultValue={defaultValue}
          accessAllowed={true}
          placeholder="Enter user message"
          setValue={handleEditMessage}
          inPlayground
          ref={inputRef}
          onKeyDown={handleKeyDown}
          focusOnMount={!isEmptyState}
        />
        <div className="input-controls">
          {!isLoading && !isEmptyState && (
            <OverlayTrigger
              placement="top"
              overlay={renderTooltip("Clear all prompts and start new session")}
            >
              <Button variant="link" disabled={isLoading} onClick={resetState}>
                <RefreshIcon size={16} />
              </Button>
            </OverlayTrigger>
          )}
          {isLoading ? (
            <div className="loader-container">
              <Spinner animation="border" size="sm" data-testid="loader" />
            </div>
          ) : (
            <>
              <Button
                className="add-button"
                onClick={handleAddMessage}
                disabled={!editorValue || !hasApiKey}
                variant="secondary"
              >
                Add
              </Button>
              <Button
                className="fw-bold flex-shrink-0 d-flex align-items-center gap column-gap-2"
                onClick={runRequest}
                disabled={
                  ((isLoading || isEmptyState) && !editorValue) ||
                  !hasApiKey ||
                  disabledSubmit
                }
              >
                Run
                <div className="icons-container">
                  {`${isMacOS ? "⌘" : "Ctrl"} `} ↵
                </div>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export { QuestionForm };
